/**
 * IA generation + persist for Conversation learning resources (backoffice).
 */

import { useMemo, useState } from 'react';
import { Sparkles, Save, RefreshCw, Volume2, Languages } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Badge } from '@/components/atoms/Badge';
import { Card } from '@/components/molecules/Card';
import type { EnglishLevel } from '@/types/story.types';
import type { ConversationSituationDocument, ContentItem } from '@/types/conversation.types';
import { ApiError } from '@/services/api/client';
import * as conversationsApi from '@/services/api/conversations.api';
import { useGenerateConversation } from '@/hooks/useGenerateConversation';
import styles from './ConversationSituationPanel.module.scss';

interface ConversationSituationPanelProps {
  resourceId: string;
  level: EnglishLevel;
  content: ContentItem[];
  onSaved: () => void;
}

function isConversationItem(item: ContentItem): item is ContentItem & {
  type: 'conversation';
  conversation: ConversationSituationDocument;
} {
  return item.type === 'conversation' && !!item.conversation?.situation;
}

function formatImageGenerationError(err: unknown): string {
  if (
    err instanceof ApiError &&
    err.data &&
    typeof err.data === 'object' &&
    'error' in err.data
  ) {
    const detail = err.data as { error?: string; detail?: string };
    return [detail.error, detail.detail].filter(Boolean).join(': ') || err.message;
  }
  return err instanceof Error ? err.message : 'Error al generar la imagen';
}

export function ConversationSituationPanel({
  resourceId,
  level,
  content,
  onSaved,
}: ConversationSituationPanelProps) {
  const [context, setContext] = useState('');
  const [studentRole, setStudentRole] = useState('');
  const [persistPhase, setPersistPhase] = useState<'idle' | 'saving' | 'image'>('idle');
  const [imageError, setImageError] = useState<string | null>(null);
  const [opError, setOpError] = useState<string | null>(null);
  /**
   * Estado del último intento de generar/regenerar audios NPC. Se nutre de la
   * respuesta del PUT (al guardar) o del endpoint dedicado de retry.
   */
  const [audioReport, setAudioReport] = useState<{
    totalLines: number;
    processedLines: number;
    missing: string[];
  } | null>(null);
  const [retryingAudios, setRetryingAudios] = useState(false);
  const [translationReport, setTranslationReport] = useState<{
    keysRequested: number;
    keysFilled: number;
    skipped?: boolean;
    message?: string;
  } | null>(null);
  const [retryingTranslations, setRetryingTranslations] = useState(false);

  const {
    status: genStatus,
    progressMessages,
    generatedDocument,
    error: genError,
    generate,
    reset: resetGen,
  } = useGenerateConversation();

  const generating = genStatus === 'generating';

  const stored = useMemo(() => content.find(isConversationItem) ?? null, [content]);

  const activeDoc = generatedDocument ?? stored?.conversation ?? null;
  const contentItemId = stored?.id ?? null;

  const handleGenerate = () => {
    if (!context.trim()) {
      setOpError('Describe el contexto de la situación (dónde, qué está en juego).');
      return;
    }
    setOpError(null);
    setImageError(null);
    setTranslationReport(null);
    void generate({
      level,
      context: context.trim(),
      studentRole: studentRole.trim() || null,
    });
  };

  const handleRetryBackgroundImage = async () => {
    setPersistPhase('image');
    setImageError(null);
    try {
      await conversationsApi.generateConversationPracticeBackgroundImage(resourceId);
      onSaved();
    } catch (e) {
      setImageError(formatImageGenerationError(e));
    } finally {
      setPersistPhase('idle');
    }
  };

  const handleSave = async () => {
    if (!activeDoc) {
      setOpError('No hay conversación para guardar. Genera primero.');
      return;
    }
    setOpError(null);
    setImageError(null);
    setAudioReport(null);
    setTranslationReport(null);
    setPersistPhase('saving');
    try {
      const saved = await conversationsApi.putConversationPracticeSituation(resourceId, {
        document: activeDoc,
        contentItemId,
      });
      if (saved.audioStats) {
        setAudioReport({
          totalLines: saved.audioStats.totalLines,
          processedLines: saved.audioStats.processedLines,
          missing: saved.missingAudios ?? [],
        });
      }
      onSaved();
    } catch (e) {
      setOpError(e instanceof Error ? e.message : 'Error al guardar');
      setPersistPhase('idle');
      return;
    }
    setPersistPhase('image');
    try {
      await conversationsApi.generateConversationPracticeBackgroundImage(resourceId);
      setImageError(null);
      onSaved();
    } catch (e) {
      setImageError(
        `Conversación guardada, pero la imagen falló: ${formatImageGenerationError(e)}`
      );
    } finally {
      setPersistPhase('idle');
    }
  };

  /**
   * Reintenta SOLO los audios NPC que fallaron previamente (idempotente). El
   * backend no regenera los que ya están guardados; aquí solo refrescamos el
   * recurso y limpiamos el report si ya están todos.
   */
  const handleRetryAudios = async () => {
    setRetryingAudios(true);
    try {
      const result = await conversationsApi.regenerateConversationPracticeAudios(resourceId);
      setAudioReport({
        totalLines: result.totalLines,
        processedLines: result.processedLines,
        missing: result.missingAudios,
      });
      onSaved();
    } catch (e) {
      setOpError(
        e instanceof Error
          ? `Reintento de audios falló: ${e.message}`
          : 'Reintento de audios falló'
      );
    } finally {
      setRetryingAudios(false);
    }
  };

  const handleTranslations = async (force: boolean) => {
    if (force) {
      const ok = window.confirm(
        'Se volverán a generar todas las traducciones al español (textos de ayuda). ¿Continuar?'
      );
      if (!ok) return;
    }
    setRetryingTranslations(true);
    setOpError(null);
    try {
      const result = await conversationsApi.regenerateConversationPracticeTranslations(resourceId, {
        force,
      });
      setTranslationReport({
        keysRequested: result.keysRequested,
        keysFilled: result.keysFilled,
        skipped: result.skipped,
        message: result.message,
      });
      resetGen();
      onSaved();
    } catch (e) {
      setOpError(
        e instanceof Error ? `Traducciones: ${e.message}` : 'Error al generar traducciones'
      );
    } finally {
      setRetryingTranslations(false);
    }
  };

  const isPersisting = persistPhase !== 'idle';

  return (
    <div className={styles.panel}>
      <p className={styles.intro}>
        Genera una conversación interactiva de <strong>5 decisiones</strong> con opciones de respuesta. La validación estructural
        se ejecuta en el servidor; si falla, regenera. Al guardar, tras persistir el JSON, se intenta generar la imagen de fondo
        en serie (mismo criterio que al aprobar una historia generada por IA).
      </p>

      <div className={styles.grid}>
        <div>
          <label className={styles.label} htmlFor={`conv-context-${resourceId}`}>
            Contexto para la IA
          </label>
          <textarea
            id={`conv-context-${resourceId}`}
            className={styles.textarea}
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Ej: Check-in temprano en un hotel tras un vuelo transatlántico. El recepcionista es profesional pero el sistema ha fallado..."
          />
        </div>
        <div>
          <label className={styles.label} htmlFor={`conv-role-${resourceId}`}>
            Rol del estudiante (opcional)
          </label>
          <Input
            id={`conv-role-${resourceId}`}
            value={studentRole}
            onChange={(e) => setStudentRole(e.target.value)}
            placeholder="Vacío = el alumno es él mismo (yourself)"
          />
        </div>
        <div className={styles.statusBar}>
          <span className={styles.label}>Nivel CEFR (desde el recurso):</span>
          <Badge variant="info">{level}</Badge>
        </div>
      </div>

      {generating && progressMessages.length > 0 && (
        <Card>
          <ul>
            {progressMessages.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </Card>
      )}

      {genError && <p className={styles.error}>{genError}</p>}
      {opError && <p className={styles.error}>{opError}</p>}
      {imageError && (
        <div className={styles.imageErrorBlock}>
          <p className={styles.error}>{imageError}</p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            leftIcon={<RefreshCw size={16} />}
            onClick={() => void handleRetryBackgroundImage()}
            disabled={isPersisting || generating}
          >
            Reintentar imagen
          </Button>
        </div>
      )}
      {audioReport && (
        <div className={styles.imageErrorBlock}>
          {audioReport.missing.length === 0 ? (
            <p className={styles.hintOk}>
              Audios NPC: {audioReport.totalLines}/{audioReport.totalLines} generados.
            </p>
          ) : (
            <>
              <p className={styles.error}>
                Audios NPC pendientes: {audioReport.missing.length} de {audioReport.totalLines} fallaron
                ({audioReport.missing.slice(0, 4).join(', ')}
                {audioReport.missing.length > 4 ? '…' : ''}).
              </p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                leftIcon={<Volume2 size={16} />}
                onClick={() => void handleRetryAudios()}
                isLoading={retryingAudios}
                disabled={retryingAudios || isPersisting || generating}
              >
                Reintentar audios pendientes
              </Button>
            </>
          )}
        </div>
      )}
      {translationReport && (
        <div className={styles.imageErrorBlock}>
          {translationReport.skipped ? (
            <p className={styles.hintOk}>{translationReport.message ?? 'Sin cambios.'}</p>
          ) : (
            <p className={styles.hintOk}>
              Traducciones ES: {translationReport.keysFilled}/{translationReport.keysRequested}{' '}
              actualizadas.
            </p>
          )}
        </div>
      )}

      <div className={styles.actionsRow}>
        <Button
          type="button"
          leftIcon={<Sparkles size={18} />}
          onClick={handleGenerate}
          isLoading={generating}
          disabled={generating || isPersisting}
        >
          Generar con IA
        </Button>
        <Button
          type="button"
          variant="secondary"
          leftIcon={<Save size={18} />}
          onClick={() => void handleSave()}
          isLoading={isPersisting}
          disabled={isPersisting || generating || !activeDoc}
        >
          {persistPhase === 'saving'
            ? 'Guardando conversación…'
            : persistPhase === 'image'
              ? 'Generando imagen…'
              : 'Guardar conversación en el recurso'}
        </Button>
        {stored && (
          <>
            <Button
              type="button"
              variant="ghost"
              leftIcon={<Volume2 size={18} />}
              onClick={() => void handleRetryAudios()}
              isLoading={retryingAudios}
              disabled={retryingAudios || isPersisting || generating || retryingTranslations}
              title="Genera los audios NPC que aún no estén disponibles (idempotente)."
            >
              Regenerar audios NPC
            </Button>
            <Button
              type="button"
              variant="ghost"
              leftIcon={<Languages size={18} />}
              onClick={() => void handleTranslations(false)}
              isLoading={retryingTranslations}
              disabled={
                retryingTranslations || isPersisting || generating || retryingAudios
              }
              title="Rellena solo las traducciones al español que falten."
            >
              Completar traducciones (ES)
            </Button>
            <Button
              type="button"
              variant="ghost"
              leftIcon={<RefreshCw size={18} />}
              onClick={() => void handleTranslations(true)}
              isLoading={retryingTranslations}
              disabled={
                retryingTranslations || isPersisting || generating || retryingAudios
              }
              title="Vuelve a generar todas las ayudas en español."
            >
              Regenerar todas las traducciones
            </Button>
          </>
        )}
      </div>

      {activeDoc && (
        <div>
          <p className={styles.label}>Vista previa (título)</p>
          <p className={styles.hintOk}>
            <strong>{activeDoc.situation.title}</strong> · {activeDoc.situation.interlocutor.name} ·{' '}
            {activeDoc.situation.turns.filter((t) => t.speaker === 'student').length} decisiones
          </p>
          <details>
            <summary className={styles.label}>JSON</summary>
            <pre className={styles.jsonPreview}>{JSON.stringify(activeDoc, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
}
